/**
 * `menuClasses` — Tailwind class-string constants used by
 * `MenuSheet.tsx`.
 *
 * Extracted from `MenuSheet.tsx` to keep the component file under the
 * 400-line architecture ceiling (`file-size-400.architecture.test.ts`,
 * FR-045) and to give the class-string surface a single-purpose home.
 * Every string here is layout (`flex`, `gap-*`, `px-*`, `py-*`) or a
 * token-backed colour utility — no raw hex, no arbitrary `*-[…]`
 * brackets (FR-002, FR-047).
 *
 * Pure data module: no imports beyond what's needed to keep these
 * strings typed, no runtime behaviour. Tests treat individual strings
 * here as part of the public contract of the menu surface (e.g. the
 * `min-h-11` hit-target invariant B-INV-025 is enforced by inspecting
 * the class strings on rendered elements).
 */

// Sheet content is laid out as a flex column so the CMS pill (see
// `menuCmsClasses` below) can use `mt-auto` to pin to the bottom edge.
// CONTRACT: any change here that drops `flex flex-col` will silently
// break the CMS pill's bottom-pinning. The `gap-3` controls the
// vertical rhythm between every direct child of `Sheet.Content`.
export const sheetContentClasses = 'flex flex-col gap-3';

export const menuHeaderClasses = 'flex items-center justify-end';
export const menuCloseButtonClasses =
  'inline-flex h-11 w-11 items-center justify-center rounded-md cursor-pointer ' +
  'text-muted-foreground transition active:scale-95 ' +
  'hover:bg-tab-active/10 hover:text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

// User chip is a full-width row with a 40 px avatar — at this size
// `active:scale-95` reads as a layout jolt rather than a tap. Press
// feedback is carried by colour deepening (`hover:bg-muted/80
// active:bg-muted/70`) instead, which matches the chip's surface
// treatment and keeps the avatar/name baseline stable on press.
export const menuChipClasses =
  'flex items-center gap-3 rounded-lg bg-muted p-3 no-underline text-foreground transition ' +
  'hover:bg-muted/80 active:bg-muted/70 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
export const menuChipAvatarClasses =
  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ' +
  'bg-primary text-primary-foreground text-sm font-semibold';
// User-chip body — flex-column with two rows (US4 / FR-010, FR-011):
//   Row 1: display name + demo-identity badge (trailing the name).
//   Row 2: "Jäsennumero: <member number>" — the member-number entry.
// `menuChipNameRowClasses` lays the row-1 children horizontally with the
// `items-center` alignment that keeps the small `text-xs` badge optically
// aligned with the larger `text-sm` name baseline. `menuChipNumberClasses`
// is the standalone row-2 span — it carries text styling only (the row's
// vertical position comes from being a child of `menuChipBodyClasses`).
export const menuChipBodyClasses = 'flex flex-col gap-0.5';
export const menuChipNameRowClasses = 'inline-flex items-center gap-2';
export const menuChipNameClasses = 'text-sm font-semibold text-foreground';
export const menuChipNumberClasses = 'text-xs text-muted-foreground';
export const menuChipBadgeClasses =
  'inline-flex items-center rounded-md bg-card px-1.5 py-0.5 text-xs font-medium ' +
  'uppercase tracking-wide text-muted-foreground';

export const menuServiceNavClasses = 'flex flex-col gap-1';
// Row treatment shared by NavLinks and the expandable parent buttons.
// Native buttons pick up `cursor-pointer` explicitly (Tailwind v4
// preflight no longer applies pointer to buttons); anchors get
// pointer naturally. The proto's `hover:bg-green-500/10` is expressed
// against our token alphabet as `hover:bg-tab-active/10`.
// `active:scale-95` carries the press-down feedback proto-parity
// calls for. `min-h-11` carries B-INV-025 / FR-015 (44 × 44 CSS-px
// hit target).
export const menuLinkClasses =
  'inline-flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-base font-medium ' +
  'text-foreground no-underline cursor-pointer transition active:scale-95 ' +
  'hover:bg-tab-active/10 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

// Disabled (membership-gated) treatment for service rows: the link row
// greys out and drops affordance cues while keeping its layout.
export const menuLinkDisabledClasses = `${menuLinkClasses} opacity-50 cursor-not-allowed`;
export const menuLinkIconClasses = 'h-5 w-5 shrink-0 text-muted-foreground';
export const menuLinkLabelClasses = 'flex-1 text-left';
// Trailing chevron on `expandable` service-nav rows ONLY — proto-parity
// disclosure-state cue (chevron-right when collapsed, chevron-down when
// expanded). Flat-link rows render as icon + label only and do NOT
// carry this affordance (B-INV-014, B-INV-015 — chevrons signal
// "this expands", not "this navigates"). Muted-foreground tint +
// opacity match the prototype's chevron weight. The same utility
// string is used for both `ChevronRightIcon` (collapsed) and
// `ChevronDownIcon` (expanded) so the two glyphs share the visual
// weight.
export const menuLinkChevronClasses = 'h-4 w-4 shrink-0 text-muted-foreground opacity-60';

// Submenu region (017/US1) — a `role="group"` `<div>` that holds the
// expandable parent's child rows when expanded. `flex-col` lays the
// children vertically; `gap-1` matches the parent service-nav rhythm;
// `pl-9` indents the children one nest-level inside the parent's left
// edge so AT and sighted users both read the hierarchy. The `mt-1`
// adds a touch of vertical breathing room between parent and first
// child without breaking the parent's own row height.
export const menuSubmenuGroupClasses = 'flex flex-col gap-1 pl-9 mt-1';

// (The submenu deferred-child class string was removed in 026/US2 with
// the Valmennukset deferred row — the menu no longer renders any
// non-interactive "Tulossa" submenu rows. Every submenu child is now an
// active NavLink styled by `menuLinkClasses`.)

export const menuDividerClasses = 'border-0 border-t border-border';

// Language picker — one full-row Select trigger. Its native button owns the
// drawer's shared interaction treatment; the trailing span preserves the
// compact value pill without becoming a nested control.
export const menuLanguageToggleClasses =
  'inline-flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-base font-medium ' +
  'text-foreground cursor-pointer transition active:scale-95 ' +
  'hover:bg-tab-active/10 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

export const menuLanguageValueClasses =
  'ml-auto inline-flex min-h-6 items-center justify-between gap-2 rounded-full border border-border ' +
  'bg-background px-3 py-1 text-sm [&>span]:line-clamp-1';

export const menuLanguageValueIconClasses = 'size-4 shrink-0 text-muted-foreground';

// Sign-out — proto pattern: full-width row, no border, no card bg, red
// text + leading sign-out icon. Hover deepens to a destructive tint
// instead of the muted-grey we used previously.
export const menuSignOutClasses =
  'inline-flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-base font-medium ' +
  'text-destructive cursor-pointer transition active:scale-95 ' +
  'hover:bg-destructive/10 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

// CMS — proto's bottom-pinned primary-fill pill. `mt-auto` pushes it
// to the bottom of the Sheet.Content's flex column so the chrome
// above can grow naturally without dislodging the pill. Navigates to
// `/admin/cms` (the authoring shell); rendered only for admin-role
// sessions (see `MenuSheet.tsx`).
export const menuCmsClasses =
  'inline-flex min-h-11 mt-auto items-center justify-center gap-3 rounded-lg ' +
  'bg-primary text-primary-foreground no-underline cursor-pointer ' +
  'px-3 py-2 text-base font-medium ' +
  'transition active:scale-95 ' +
  'hover:bg-primary/90 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
